import { useMemo } from "react";
import { computeTracker, MONTH_LABELS } from "@/lib/calc";
import { formatINR, formatPct, parseNumber } from "@/lib/format";

const StatusPill = ({ status }) => {
  const cls =
    status === "On Target"
      ? "pill pill-green"
      : status === "Approaching (11x)"
      ? "pill pill-amber"
      : status === "Break-even Zone"
      ? "pill pill-slate"
      : "pill pill-red";
  return <span className={cls}>{status}</span>;
};

export const MonthlyTracker = ({ summary, actuals, setActuals }) => {
  const { rows, totals } = useMemo(
    () => computeTracker(summary, actuals),
    [summary, actuals]
  );

  const handleChange = (idx, raw) => {
    const next = [...actuals];
    next[idx] = parseNumber(raw);
    setActuals(next);
  };

  return (
    <section
      className="border border-slate-200 bg-white print-block"
      data-testid="monthly-tracker-section"
    >
      <div className="p-6 border-b border-slate-200 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
            Monthly Revenue &amp; Incentive Tracker
          </p>
          <h3 className="font-display text-2xl mt-1 text-slate-900">
            Enter actual revenue — everything else auto-updates
          </h3>
        </div>
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-slate-700">Tip:</span> enter actual revenue in
          the yellow cells; scenario earnings, variance and status recompute instantly.
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="tracker-table">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase tracking-wider text-slate-600">
              <Th sticky>Month</Th>
              <Th>Monthly Target 15×</Th>
              <Th>Monthly Target 11×</Th>
              <Th>Cumulative Target</Th>
              <Th className="bg-amber-50">Actual Revenue</Th>
              <Th>Target Achv %</Th>
              <Th>Rev Multiple</Th>
              <Th className="border-l border-slate-200">S1 Monthly Payout</Th>
              <Th>S1 Pro-rata Incentive</Th>
              <Th>S1 Incentive Payable</Th>
              <Th>S1 Total Earnings</Th>
              <Th className="border-l border-slate-200">S2 Monthly Payout</Th>
              <Th>S2 Variable @15×</Th>
              <Th>S2 Incentive @11×</Th>
              <Th>S2 Total Earnings</Th>
              <Th>Variance to Target</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.month}
                className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors duration-150 fade-up"
                style={{ animationDelay: `${i * 20}ms` }}
                data-testid={`tracker-row-${r.month}`}
              >
                <Td sticky>
                  <span className="font-semibold text-slate-900">{r.month}</span>
                </Td>
                <Td num>{formatINR(r.monthlyTarget15)}</Td>
                <Td num>{formatINR(r.monthlyTarget11)}</Td>
                <Td num>{formatINR(r.cumulativeTarget)}</Td>
                <Td className="bg-amber-50/40 p-0">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={actuals[i] || ""}
                    onChange={(e) => handleChange(i, e.target.value)}
                    className="w-full h-full px-3 py-2 bg-transparent text-right font-mono-num text-slate-900 focus:outline-none focus:bg-amber-100/50 focus:ring-1 focus:ring-inset focus:ring-amber-500"
                    placeholder="0"
                    data-testid={`input-actual-${r.month}`}
                  />
                </Td>
                <Td num accent={r.achievementPct >= 100 ? "success" : r.achievementPct < 67 ? "danger" : ""}>
                  {formatPct(r.achievementPct)}
                </Td>
                <Td num>{r.revenueMultiple.toFixed(2)}×</Td>
                <Td num className="border-l border-slate-200">{formatINR(r.s1_monthly_payout)}</Td>
                <Td num>{formatINR(r.s1_pro_rata_incentive)}</Td>
                <Td num>{formatINR(r.s1_incentive_payable)}</Td>
                <Td num accent="primary">{formatINR(r.s1_total_earnings)}</Td>
                <Td num className="border-l border-slate-200">{formatINR(r.s2_monthly_payout)}</Td>
                <Td num accent={r.s2_variable_15x > 0 ? "success" : "muted"}>{formatINR(r.s2_variable_15x)}</Td>
                <Td num accent={r.s2_incentive_11x > 0 ? "success" : "muted"}>{formatINR(r.s2_incentive_11x)}</Td>
                <Td num accent="primary">{formatINR(r.s2_total_earnings)}</Td>
                <Td num accent={r.variance >= 0 ? "success" : "danger"}>
                  {r.variance >= 0 ? "+" : ""}
                  {formatINR(r.variance)}
                </Td>
                <Td><StatusPill status={r.status} /></Td>
              </tr>
            ))}

            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="tracker-totals-row">
              <Td sticky><span className="text-slate-900">YEAR TOTAL</span></Td>
              <Td num>{formatINR(summary.target15)}</Td>
              <Td num>{formatINR(summary.target11)}</Td>
              <Td num>{formatINR(summary.target15)}</Td>
              <Td num>{formatINR(totals.actualRevenue)}</Td>
              <Td num>{formatPct(summary.target15 > 0 ? (totals.actualRevenue / summary.target15) * 100 : 0)}</Td>
              <Td num>{(summary.annual > 0 ? totals.actualRevenue / summary.annual : 0).toFixed(2)}×</Td>
              <Td num className="border-l border-slate-200">{formatINR(totals.s1_monthly_payout)}</Td>
              <Td num>{formatINR(totals.s1_pro_rata_incentive)}</Td>
              <Td num>{formatINR(totals.s1_pro_rata_incentive)}</Td>
              <Td num accent="primary">{formatINR(totals.s1_total_earnings)}</Td>
              <Td num className="border-l border-slate-200">{formatINR(totals.s2_monthly_payout)}</Td>
              <Td num>{formatINR(totals.s2_variable_15x)}</Td>
              <Td num>{formatINR(totals.s2_incentive_11x)}</Td>
              <Td num accent="primary">{formatINR(totals.s2_total_earnings)}</Td>
              <Td num accent={totals.actualRevenue - summary.target15 >= 0 ? "success" : "danger"}>
                {formatINR(totals.actualRevenue - summary.target15)}
              </Td>
              <Td>—</Td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

const Th = ({ children, sticky, className = "" }) => (
  <th
    className={`px-3 py-3 text-left font-semibold whitespace-nowrap ${
      sticky ? "sticky left-0 bg-slate-50 z-10" : ""
    } ${className}`}
  >
    {children}
  </th>
);

const Td = ({ children, num, sticky, accent, className = "" }) => {
  const accentCls =
    accent === "success"
      ? "text-emerald-700"
      : accent === "danger"
      ? "text-rose-700"
      : accent === "primary"
      ? "text-blue-800 font-semibold"
      : accent === "muted"
      ? "text-slate-400"
      : "text-slate-800";
  return (
    <td
      className={`px-3 py-2 whitespace-nowrap ${
        num ? "num-cell text-sm " + accentCls : accentCls
      } ${sticky ? "sticky left-0 bg-white z-10" : ""} ${className}`}
    >
      {children}
    </td>
  );
};

export default MonthlyTracker;
